import {
    Box,
    Typography,
    Card,
    Grid,
    Avatar,
    Chip,
    Divider,
    Paper
} from '@mui/material'
import {
    CalendarToday as CalendarIcon,
    Pets as PetsIcon,
    Home as HomeIcon,
    People as PeopleIcon,
    LocationOn as LocationIcon,
    Phone as PhoneIcon
} from '@mui/icons-material'
import { formatPhoneNumber } from '../../utils/formatPhoneNumber'

/** Must match `TourRequest::UNSPECIFIED_LISTING_URL` */
const TOUR_REQUEST_UNSPECIFIED_LISTING_URL = 'https://heyyproperty.com/tour-listing-unspecified'
function isDisplayableTourListingUrl(url: string | null | undefined): boolean {
    return Boolean(url && url !== TOUR_REQUEST_UNSPECIFIED_LISTING_URL)
}

interface ProspectData {
    id: string;
    name: string;
    email: string;
    phone_number: string | null;
    created_at: string;
    updated_at: string;
    search_status: string;
    credit_score: number | null;
    age_range: string | null;
    new_renter: boolean | null;
    has_year_long_rental: boolean | null;
    current_living_situation: string | null;
    living_situation_explained: string | null;
    student: boolean | null;
    undergraduate: boolean | null;
    currently_employed: boolean | null;
    rent_payment_without_job: string | null;
    explain_rent_payment: string | null;
    income_verifiable: boolean | null;
    has_current_job_for_year: boolean | null;
    has_one_year_employment_history_last_3_years: boolean | null;
    will_do_background_and_credit_check: boolean | null;
    num_cats: number | null;
    num_dogs: number | null;
    lease_length_requested: string | null;
    move_in_date_requested: string | null;
    moving_reason: string | null;
    number_of_applicants: string | null;
    tour_preference: string | null;
    additional_search_info: string | null;
    budget: number | null;
    employer: string | null;
    occupation: string | null;
    neighborhoods: string | null;
    num_bedrooms_wanted: number | null;
    num_bathrooms_wanted: number | null;
    housing_voucher: boolean | null;
    short_intro: string | null;
    profile_pic_url: string | null;
    rental_type: number | null;
    short_code: string | null;
}

interface TourRequest {
    id: string;
    status: string;
    listing_address: string;
    listing_url: string;
    rent: number;
    neighborhood: string | null;
    bedrooms: number | null;
    bathrooms: number | null;
    landlord_intro_message: string | null;
    match_rating: number | null;
    rejection_reason: string | null;
    tour_giver_name: string | null;
    listing_contact_email: string;
    created_at: string;
    tour_slot: {
        id: string;
        date: string;
        start_time_local: string;
        end_time_local: string;
    };
}

interface AdminProfileCardProps {
    prospect: ProspectData;
    tour_requests: TourRequest[];
    upcoming_tours: (TourRequest & { is_past?: boolean })[];
}

export default function AdminProfileCard({ prospect, tour_requests, upcoming_tours }: AdminProfileCardProps) {
    const formatSearchStatus = (status: string) => {
        return status.replace('_', ' ').split(' ').map(word =>
            word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const formatRentalType = (type: number | null) => {
        if (type === null) return 'Not specified';
        return type === 0 ? 'Private Room' : 'Entire Place';
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'pending':
                return 'warning';
            case 'declined':
                return 'error';
            case 'expired':
                return 'default';
            case 'accepted':
                return 'success';
            default:
                return 'default';
        }
    };

    const getStatusLabel = (status: string) => {
        return status.charAt(0).toUpperCase() + status.slice(1);
    };

    const firstName = prospect.name?.split(' ')[0] || '';
    const lastInitial = prospect.name?.split(' ').slice(1).map(n => n[0]).join('.') || '';

    return (
        <Box>
            <Card sx={{ p: 4, mb: 3, borderRadius: '16px', overflow: 'hidden', boxShadow: '0px 0px 4px 0px rgba(0,0,0,0.2)' }}>
                {/* Basic Info */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar
                        src={prospect.profile_pic_url || undefined}
                        sx={{
                            width: 100,
                            height: 100,
                            bgcolor: 'grey.300',
                            fontSize: '3rem',
                            mr: 3
                        }}
                    >
                        {!prospect.profile_pic_url && (firstName?.[0] || '?')}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                            {prospect.name}
                        </Typography>
                        {prospect.occupation && (
                            <Typography variant="h6" color="grey.600" sx={{ mb: 1 }}>
                                {prospect.occupation}
                            </Typography>
                        )}
                        {prospect.employer && (
                            <Typography variant="body1" color="grey.500" sx={{ mb: 1 }}>
                                {prospect.employer}
                            </Typography>
                        )}
                        <Chip
                            label={formatSearchStatus(prospect.search_status)}
                            size="small"
                            color="primary"
                            sx={{ mb: 1 }}
                        />
                        <Typography variant="body2" color="text.secondary">
                            Created: {formatDate(prospect.created_at)}
                        </Typography>
                        {prospect.email && (
                            <Typography variant="body2" color="text.secondary">
                                Email: {prospect.email}
                            </Typography>
                        )}
                        {prospect.phone_number && (
                            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PhoneIcon sx={{ fontSize: 16 }} />
                                Phone: {formatPhoneNumber(prospect.phone_number)}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Introduction */}
                {prospect.short_intro && (
                    <>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                            About Me
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                            {prospect.short_intro}
                        </Typography>
                        <Divider sx={{ my: 3 }} />
                    </>
                )}

                {/* Rental Preferences */}
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                    Rental Preferences
                </Typography>

                <Grid container spacing={3} sx={{ mb: 3 }}>
                    {prospect.tour_preference && (
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <HomeIcon sx={{ fontSize: 20, color: 'grey.600' }} />
                                <Typography variant="body2" color="grey.600">
                                    Tour Preference
                                </Typography>
                            </Box>
                            <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                                {prospect.tour_preference.replace('_', ' ')}
                            </Typography>
                        </Grid>
                    )}
                    {prospect.rental_type !== null && (
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <HomeIcon sx={{ fontSize: 20, color: 'grey.600' }} />
                                <Typography variant="body2" color="grey.600">
                                    Rental Type
                                </Typography>
                            </Box>
                            <Typography variant="h6">
                                {formatRentalType(prospect.rental_type)}
                            </Typography>
                        </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <HomeIcon sx={{ fontSize: 20, color: 'grey.600' }} />
                            <Typography variant="body2" color="grey.600">
                                Looking for
                            </Typography>
                        </Box>
                        <Typography variant="h6">
                            {prospect.num_bedrooms_wanted || 'Not specified'} bed • {prospect.num_bathrooms_wanted || 'Not specified'} bath
                        </Typography>
                    </Grid>

                    {prospect.move_in_date_requested && (
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <CalendarIcon sx={{ fontSize: 20, color: 'grey.600' }} />
                                <Typography variant="body2" color="grey.600">
                                    Move-in Date
                                </Typography>
                            </Box>
                            <Typography variant="h6">
                                {prospect.move_in_date_requested ? new Date(`${prospect.move_in_date_requested}T12:00:00`).toLocaleDateString('en-US', {
                                    month: 'long',
                                    day: 'numeric'
                                }) : 'Not specified'}
                            </Typography>
                        </Grid>
                    )}

                    <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <PetsIcon sx={{ fontSize: 20, color: 'grey.600' }} />
                            <Typography variant="body2" color="grey.600">
                                Dogs
                            </Typography>
                        </Box>
                        <Typography variant="h6">
                            {prospect.num_dogs || '0'}
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <PetsIcon sx={{ fontSize: 20, color: 'grey.600' }} />
                            <Typography variant="body2" color="grey.600">
                                Cats
                            </Typography>
                        </Box>
                        <Typography variant="h6">
                            {prospect.num_cats || '0'}
                        </Typography>
                    </Grid>

                    {prospect.number_of_applicants && (
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <PeopleIcon sx={{ fontSize: 20, color: 'grey.600' }} />
                                <Typography variant="body2" color="grey.600">
                                    Occupants
                                </Typography>
                            </Box>
                            <Typography variant="h6">
                                {prospect.number_of_applicants} {prospect.number_of_applicants !== '1' ? 'people' : 'person'}
                            </Typography>
                        </Grid>
                    )}

                    {prospect.budget && (
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <HomeIcon sx={{ fontSize: 20, color: 'grey.600' }} />
                                <Typography variant="body2" color="grey.600">
                                    Budget
                                </Typography>
                            </Box>
                            <Typography variant="h6">
                                ${prospect.budget.toLocaleString()}
                            </Typography>
                        </Grid>
                    )}
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Additional Details */}
                <Grid container spacing={3}>
                    {prospect.lease_length_requested && (
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="grey.600" sx={{ mb: 1 }}>
                                Lease Length
                            </Typography>
                            <Chip
                                label={prospect.lease_length_requested.replace('-', ' ')}
                                variant="outlined"
                                sx={{ textTransform: 'capitalize' }}
                            />
                        </Grid>
                    )}

                    {prospect.housing_voucher && (
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="grey.600" sx={{ mb: 1 }}>
                                Housing Voucher
                            </Typography>
                            <Chip
                                label="Yes"
                                color="success"
                                variant="outlined"
                            />
                        </Grid>
                    )}

                    {prospect.neighborhoods && (
                        <Grid item xs={12}>
                            <Typography variant="body2" color="grey.600" sx={{ mb: 1 }}>
                                Preferred Neighborhoods
                            </Typography>
                            <Typography variant="body1">
                                {prospect.neighborhoods}
                            </Typography>
                        </Grid>
                    )}

                    {prospect.additional_search_info && (
                        <Grid item xs={12}>
                            <Typography variant="body2" color="grey.600" sx={{ mb: 1 }}>
                                Additional Information
                            </Typography>
                            <Typography variant="body1">
                                {prospect.additional_search_info}
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </Card>

            {/* Tour Requests Section */}
            <Card sx={{ p: 4, mb: 3, borderRadius: '16px', overflow: 'hidden', boxShadow: '0px 0px 4px 0px rgba(0,0,0,0.2)' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                    Tour Requests (Pending, Declined, Expired)
                </Typography>
                {tour_requests.length > 0 ? (
                    tour_requests.map((request) => (
                        <Paper key={request.id} sx={{ p: 3, mb: 2, bgcolor: 'grey.50', borderRadius: '8px' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                        {request.listing_address}
                                    </Typography>
                                    <Chip
                                        label={getStatusLabel(request.status)}
                                        color={getStatusColor(request.status) as any}
                                        size="small"
                                        sx={{ mb: 1 }}
                                    />
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        ${request.rent.toLocaleString()}/mo
                                    </Typography>
                                    {request.bedrooms && request.bathrooms && (
                                        <Typography variant="body2" color="text.secondary">
                                            {request.bedrooms} bed • {request.bathrooms} bath
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                            {request.tour_giver_name && (
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Landlord:</strong> {request.tour_giver_name}
                                </Typography>
                            )}
                            {request.landlord_intro_message && (
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Message:</strong> {request.landlord_intro_message}
                                </Typography>
                            )}
                            {request.rejection_reason && (
                                <Typography variant="body2" color="error.main" sx={{ mb: 1 }}>
                                    <strong>Rejection Reason:</strong> {request.rejection_reason}
                                </Typography>
                            )}
                            {isDisplayableTourListingUrl(request.listing_url) && (
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Listing:</strong>{' '}
                                    <a href={request.listing_url} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
                                        View Listing
                                    </a>
                                </Typography>
                            )}
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontSize: '0.75rem' }}>
                                Tour Request ID: {request.id}
                            </Typography>
                        </Paper>
                    ))
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        No tour requests found
                    </Typography>
                )}
            </Card>

            {/* Upcoming Tours Section */}
            <Card sx={{ p: 4, mb: 3, borderRadius: '16px', overflow: 'hidden', boxShadow: '0px 0px 4px 0px rgba(0,0,0,0.2)' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                    Upcoming Tours (Accepted)
                </Typography>
                {upcoming_tours.length > 0 ? (
                    <>
                        {/* Future Tours */}
                        {upcoming_tours.filter(t => !t.is_past).length > 0 && (
                            <>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'success.main' }}>
                                    Upcoming
                                </Typography>
                                {upcoming_tours.filter(t => !t.is_past).map((tour) => (
                                    <Paper key={tour.id} sx={{ p: 3, mb: 2, bgcolor: 'success.light', borderRadius: '8px', border: '1px solid', borderColor: 'success.main' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <LocationIcon sx={{ color: 'success.main' }} />
                                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                {tour.listing_address}
                                            </Typography>
                                        </Box>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                    <CalendarIcon sx={{ fontSize: 20, color: 'success.main' }} />
                                                    <Typography variant="body1">
                                                        <strong>Date:</strong> {formatDate(tour.tour_slot.date)}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body1" sx={{ ml: 4 }}>
                                                    <strong>Time:</strong> {tour.tour_slot.start_time_local} - {tour.tour_slot.end_time_local}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                    ${tour.rent.toLocaleString()}/mo
                                                </Typography>
                                                {tour.bedrooms && tour.bathrooms && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        {tour.bedrooms} bed • {tour.bathrooms} bath
                                                    </Typography>
                                                )}
                                            </Grid>
                                        </Grid>
                                        {tour.tour_giver_name && (
                                            <Typography variant="body2" sx={{ mt: 1 }}>
                                                <strong>Landlord:</strong> {tour.tour_giver_name}
                                            </Typography>
                                        )}
                                        {isDisplayableTourListingUrl(tour.listing_url) && (
                                            <Typography variant="body2" sx={{ mt: 1 }}>
                                                <a href={tour.listing_url} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
                                                    View Listing
                                                </a>
                                            </Typography>
                                        )}
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontSize: '0.75rem' }}>
                                            Tour Request ID: {tour.id}
                                        </Typography>
                                    </Paper>
                                ))}
                            </>
                        )}

                        {/* Past Tours */}
                        {upcoming_tours.filter(t => t.is_past).length > 0 && (
                            <>
                                <Divider sx={{ my: 3 }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary' }}>
                                    Past Tours
                                </Typography>
                                {upcoming_tours.filter(t => t.is_past).map((tour) => (
                                    <Paper key={tour.id} sx={{ p: 3, mb: 2, bgcolor: 'grey.100', borderRadius: '8px', border: '1px solid', borderColor: 'grey.300' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <Chip label="Past" size="small" color="default" sx={{ mr: 1 }} />
                                            <LocationIcon sx={{ color: 'text.secondary' }} />
                                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                {tour.listing_address}
                                            </Typography>
                                        </Box>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                    <CalendarIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                                                    <Typography variant="body1">
                                                        <strong>Date:</strong> {formatDate(tour.tour_slot.date)}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body1" sx={{ ml: 4 }}>
                                                    <strong>Time:</strong> {tour.tour_slot.start_time_local} - {tour.tour_slot.end_time_local}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                    ${tour.rent.toLocaleString()}/mo
                                                </Typography>
                                                {tour.bedrooms && tour.bathrooms && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        {tour.bedrooms} bed • {tour.bathrooms} bath
                                                    </Typography>
                                                )}
                                            </Grid>
                                        </Grid>
                                        {tour.tour_giver_name && (
                                            <Typography variant="body2" sx={{ mt: 1 }}>
                                                <strong>Landlord:</strong> {tour.tour_giver_name}
                                            </Typography>
                                        )}
                                        {isDisplayableTourListingUrl(tour.listing_url) && (
                                            <Typography variant="body2" sx={{ mt: 1 }}>
                                                <a href={tour.listing_url} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
                                                    View Listing
                                                </a>
                                            </Typography>
                                        )}
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontSize: '0.75rem' }}>
                                            Tour Request ID: {tour.id}
                                        </Typography>
                                    </Paper>
                                ))}
                            </>
                        )}
                    </>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        No upcoming tours found
                    </Typography>
                )}
            </Card>
        </Box>
    );
}

